import { Alert, Button } from "antd";
import { useDismissedAnnouncement } from "hooks/useDismissedAnnouncements";
import { Link } from "react-router-dom";

export interface AnnouncementBannerProps {
  /** Unique identifier for localStorage persistence */
  id: string;
  /** Main banner text */
  message: string;
  /** Optional description or additional content */
  description?: React.ReactNode;
  /** Alert type - defaults to "info" */
  type?: "info" | "warning";
  /** Optional call-to-action link or button */
  action?: {
    label: string;
    /** Internal route */
    to?: string;
    /** External link */
    href?: string;
    /** Custom click handler */
    onClick?: () => void;
  };
  /** Whether the banner can be dismissed - defaults to true */
  closable?: boolean;
  /** Optional style overrides */
  style?: React.CSSProperties;
}

/**
 * A reusable banner component for displaying page-level announcements.
 * Supports dismissal with localStorage persistence so users don't see
 * the same message repeatedly.
 */
export const AnnouncementBanner: React.FC<AnnouncementBannerProps> = ({
  id,
  message,
  description,
  type = "info",
  action,
  closable = true,
  style,
}) => {
  const [isDismissed, dismiss] = useDismissedAnnouncement(id);

  if (isDismissed) {
    return null;
  }

  const renderAction = () => {
    if (!action) return null;

    const button = (
      <Button
        type="link"
        style={{ padding: 0, height: "auto" }}
        onClick={action.onClick}
      >
        {action.label}
      </Button>
    );

    if (action.to) {
      return <Link to={action.to}>{button}</Link>;
    }

    if (action.href) {
      return (
        <a href={action.href} target="_blank" rel="noopener noreferrer">
          {button}
        </a>
      );
    }

    return button;
  };

  return (
    <Alert
      message={message}
      description={
        description || action ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              alignItems: "flex-start",
            }}
          >
            {description && <div>{description}</div>}
            {renderAction()}
          </div>
        ) : undefined
      }
      type={type}
      showIcon
      closable={closable}
      onClose={dismiss}
      style={{ marginBottom: 16, ...style }}
    />
  );
};
